/**
 * Sign in with Apple 네이티브 wrapper — AppleLoginPlugin(iOS) 호출.
 *
 * iOS: ios-templates/App/App/AppleLoginPlugin.swift (ASAuthorizationController, 외부 SDK 불필요)
 * Android: 네이티브 Apple SDK 가 없으므로 미지원 → null 반환 (호출자가 웹 OAuth redirect 로 fallback)
 *
 * 흐름:
 *   isNative() && iOS → registerPlugin('AppleLogin').login() → { identityToken, name? }
 *
 * Apple 특성: 이름/이메일은 "최초 인증 1회"만 제공된다. 재로그인 시 name 은 undefined.
 *
 * BE 와의 contract: identityToken 을 `/identity/verify/start/apple` 의 idToken 필드로,
 *   name 을 (있을 때만) name 필드로 전달.
 */
import { registerPlugin } from "@capacitor/core";
import { Capacitor } from "@capacitor/core";
import { isNative, nativeWarn } from "../index";
import { buildAppleNativeAuthorizeUrl } from "../../lib/socialAuth";

interface AppleLoginNativePlugin {
  login(): Promise<{
    identityToken: string;
    authorizationCode?: string;
    name?: string;
    email?: string;
  }>;
}

const AppleLogin = registerPlugin<AppleLoginNativePlugin>("AppleLogin");

export interface NativeAppleResult {
  idToken: string;
  name?: string;
}

/**
 * 네이티브(iOS) 환경에서 Apple 로그인 진행 → { idToken, name? } 반환.
 * iOS 가 아니거나 웹이면 null 반환 (호출자가 웹 OAuth redirect 흐름으로 진입).
 */
export async function nativeAppleLogin(): Promise<NativeAppleResult | null> {
  if (!isNative() || Capacitor.getPlatform() !== "ios") return null;
  try {
    const result = await AppleLogin.login();
    return { idToken: result.identityToken, name: result.name };
  } catch (err) {
    nativeWarn("apple-login", err);
    throw err;
  }
}

/**
 * Android 네이티브 — Apple 은 네이티브 SDK 가 없어 웹 OAuth 를 쓴다.
 * 단 Apple 은 임베디드 WebView 로그인을 차단하므로 메인 WebView(window.location)가 아니라
 * 시스템 Custom Tab(@capacitor/browser, Android=Chrome Custom Tabs / iOS=SFSafariVC)으로 띄운다.
 *
 * 복귀: 인증 완료 → Apple form_post → BE web-callback → state 의 native 마커를 보고
 *   `peeka://oauth/apple/callback?code=&state=&name=` 로 302 → RootLayout 의 deepLink 핸들러가
 *   {@link closeAppleBrowser} 로 Custom Tab 을 닫고 인앱 `/oauth/apple/callback` 라우트로 보낸다.
 *   state 는 메인 WebView 의 sessionStorage/쿠키에 남아 있어 CSRF 검증을 통과한다.
 */
export async function startNativeAppleWebFlow(): Promise<void> {
  const url = buildAppleNativeAuthorizeUrl();
  const { Browser } = await import("@capacitor/browser");
  await Browser.open({ url });
}

/** Apple 로그인 복귀 딥링크 수신 후 Custom Tab 을 닫는다. 이미 닫혔으면 무해. */
export async function closeAppleBrowser(): Promise<void> {
  if (!isNative()) return;
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.close();
  } catch (err) {
    nativeWarn("apple-browser-close", err);
  }
}
